import type { OoxmlParagraphMeta } from "./types";

/** Word OOXML lengths are typically in twips (1/20 pt, 1/1440 inch). */

export function twipsToPt(twips: number): number {
  return twips / 20;
}

/** pt at 96dpi → px (matches docxImport export convention). */
export function ptToPx(pt: number): number {
  return Math.round(pt / 0.75);
}

export function twipsToPx(twips: number): number {
  return ptToPx(twipsToPt(twips));
}

/** US Letter content width at 96dpi (8.5in − 1in margins). */
export const DEFAULT_PAGE_WIDTH_PX = 612;

/**
 * Parse a CSS length value to pixels.
 * Supports pt, in, cm, mm, px, and % (when contextWidthPx is provided).
 */
export function parseCssLengthToPx(
  value: string | null | undefined,
  contextWidthPx?: number,
): number | null {
  if (!value) return null;
  const trimmed = value.trim().toLowerCase();
  const num = parseFloat(trimmed);
  if (Number.isNaN(num)) return null;
  if (trimmed.endsWith("%")) {
    const ctx = contextWidthPx ?? DEFAULT_PAGE_WIDTH_PX;
    return Math.round((num / 100) * ctx);
  }
  if (trimmed.endsWith("pt")) return ptToPx(num);
  if (trimmed.endsWith("in")) return Math.round(num * 96);
  if (trimmed.endsWith("cm")) return Math.round(num * 37.795);
  if (trimmed.endsWith("mm")) return Math.round(num * 3.7795);
  if (trimmed.endsWith("px")) return Math.round(num);
  return Math.round(num);
}

export function parseTwips(value: string | null | undefined): number | null {
  if (value == null || value === "") return null;
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? null : n;
}

/**
 * Convert w:spacing/@line and @lineRule to a CSS line-height value.
 * - auto: line is in 240ths of a line (240 = single, 360 = 1.5, 480 = double)
 * - exact / atLeast: line is twips (absolute height)
 */
export function lineSpacingToCss(
  line: string | null | undefined,
  lineRule: string | null | undefined,
): string | null {
  if (!line) return null;
  const n = parseInt(line, 10);
  if (Number.isNaN(n)) return null;

  const rule = (lineRule ?? "auto").toLowerCase();

  if (rule === "auto") {
    const multiplier = n / 240;
    if (Math.abs(multiplier - 1) < 0.02) return "1";
    if (Math.abs(multiplier - 1.15) < 0.02) return "1.15";
    if (Math.abs(multiplier - 1.5) < 0.02) return "1.5";
    if (Math.abs(multiplier - 2) < 0.02) return "2";
    return String(Math.round(multiplier * 100) / 100);
  }

  if (rule === "exact" || rule === "atleast") {
    return `${twipsToPt(n)}pt`;
  }

  return null;
}

export function jcToTextAlign(
  jc: string | null | undefined,
): OoxmlParagraphMeta["alignment"] | undefined {
  if (!jc) return undefined;
  switch (jc) {
    case "center":
      return "center";
    case "right":
      return "right";
    case "both":
    case "distribute":
    case "thaidistribute":
      return "justify";
    case "left":
      return "left";
    default:
      return undefined;
  }
}
